// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Everything the editor holds: loaded boards, and the panels being built.
 *
 * Boards are a **shared pool**. Several panels in one session normally draw on
 * the same set of `.kicad_pcb` files, and parsing a 33 MB board takes about a
 * second, so a file is read once and every panel refers to it by name.
 *
 * Panels each keep their own spec, selection and undo history, so switching tabs
 * never disturbs work in progress on another.
 */

import type { DisplayList } from '../render/index.ts';
import type { Board } from '../model/index.ts';
import type { Polygon } from '../panel/outline.ts';
import type { Annotation, SourceAnalysis } from '../panel/sources.ts';
import { resolveSource } from '../panel/sources.ts';
import type { SourceProvider } from '../panel/preview.ts';
import { emptySpec, type PanelSpec } from '../panel/spec.ts';

export interface SourceFile {
  /** Basename, which is also what `boards[].file` records. */
  name: string;
  board: Board;
  /** Built once and shared by every placement of this board. */
  displayList: DisplayList;
  polygons: Polygon[];
  annotations: Annotation[];
  analysis: SourceAnalysis;
  /** Widest Edge.Cuts stroke, which widens KiKit's item-selection box. */
  maxEdgeWidth: number;
  bytes: number;
}

export type SelectionKind = 'board' | 'rail' | 'patch' | 'tab' | 'text';

export interface Selection {
  kind: SelectionKind;
  index: number;
}

export interface PanelDoc {
  id: string;
  name: string;
  /** The spec file this panel came from or was last saved as, if any. */
  fileName?: string;
  spec: PanelSpec;
  selection: Selection | null;
  dirty: boolean;
  undo: string[];
  redo: string[];
}

/** Whole-spec snapshots, as the original tool did. Cheap, and impossible to get wrong. */
const UNDO_LIMIT = 100;

let nextId = 1;

function makePanel(name: string, spec: PanelSpec = emptySpec()): PanelDoc {
  return { id: `p${nextId++}`, name, spec, selection: null, dirty: false, undo: [], redo: [] };
}

export class Session {
  readonly sources = new Map<string, SourceFile>();
  panels: PanelDoc[] = [makePanel('panel-1')];
  activeId: string = this.panels[0]!.id;

  get active(): PanelDoc {
    return this.panels.find((p) => p.id === this.activeId) ?? this.panels[0]!;
  }

  get spec(): PanelSpec {
    return this.active.spec;
  }

  // --- panels -----------------------------------------------------------------------

  addPanel(): PanelDoc {
    const used = new Set(this.panels.map((p) => p.name));
    let n = this.panels.length + 1;
    while (used.has(`panel-${n}`)) n++;
    const panel = makePanel(`panel-${n}`);
    this.panels.push(panel);
    this.activeId = panel.id;
    return panel;
  }

  duplicatePanel(id: string): PanelDoc | null {
    const source = this.panels.find((p) => p.id === id);
    if (source === undefined) return null;
    const copy = makePanel(`${source.name}-copy`, structuredClone(source.spec));
    copy.dirty = true;
    this.panels.splice(this.panels.indexOf(source) + 1, 0, copy);
    this.activeId = copy.id;
    return copy;
  }

  closePanel(id: string): void {
    const index = this.panels.findIndex((p) => p.id === id);
    if (index === -1) return;
    this.panels.splice(index, 1);
    if (this.panels.length === 0) this.panels.push(makePanel('panel-1'));
    if (this.activeId === id) {
      this.activeId = this.panels[Math.min(index, this.panels.length - 1)]!.id;
    }
  }

  /** Adopt a spec loaded from a file into the active panel. */
  loadSpec(name: string, spec: PanelSpec): void {
    const panel = this.active;
    panel.name = name.replace(/\.(ya?ml|json)$/i, '');
    panel.fileName = basename(name);
    panel.spec = spec;
    panel.selection = null;
    panel.dirty = false;
    panel.undo = [];
    panel.redo = [];
  }

  // --- editing ----------------------------------------------------------------------

  /**
   * Record the current state as an undo point.
   *
   * Separate from `apply` because a drag edits the spec continuously to keep the
   * canvas live: the snapshot has to be taken *before* the first of those edits,
   * or undo restores the already-moved state and appears to do nothing.
   */
  snapshot(): void {
    const panel = this.active;
    panel.undo.push(JSON.stringify(panel.spec));
    if (panel.undo.length > UNDO_LIMIT) panel.undo.shift();
    panel.redo = [];
  }

  /** Change the spec without adding an undo point. */
  apply(change: (spec: PanelSpec) => void): void {
    const panel = this.active;
    change(panel.spec);
    panel.dirty = true;
  }

  /** One discrete edit: snapshot, then change. */
  mutate(change: (spec: PanelSpec) => void): void {
    this.snapshot();
    this.apply(change);
  }

  undo(): boolean {
    const panel = this.active;
    const previous = panel.undo.pop();
    if (previous === undefined) return false;
    panel.redo.push(JSON.stringify(panel.spec));
    panel.spec = JSON.parse(previous) as PanelSpec;
    panel.dirty = true;
    this.clampSelection();
    return true;
  }

  redo(): boolean {
    const panel = this.active;
    const next = panel.redo.pop();
    if (next === undefined) return false;
    panel.undo.push(JSON.stringify(panel.spec));
    panel.spec = JSON.parse(next) as PanelSpec;
    panel.dirty = true;
    this.clampSelection();
    return true;
  }

  select(selection: Selection | null): void {
    this.active.selection = selection;
  }

  /** Undo can shorten a list out from under the selection. */
  private clampSelection(): void {
    const panel = this.active;
    const s = panel.selection;
    if (s === null) return;
    const list = this.listFor(s.kind);
    if (s.index >= list.length) panel.selection = null;
  }

  listFor(kind: SelectionKind): unknown[] {
    const spec = this.spec;
    switch (kind) {
      case 'board': return spec.boards ?? [];
      case 'rail': return spec.rails ?? [];
      case 'patch': return spec.patches ?? [];
      case 'tab': return spec.tabs ?? [];
      case 'text': return spec.text ?? [];
    }
  }

  // --- sources ----------------------------------------------------------------------

  addSource(file: SourceFile): void {
    this.sources.set(file.name, file);
  }

  removeSource(name: string): void {
    this.sources.delete(name);
  }

  /**
   * Resolve `boards[].file` against the loaded pool.
   *
   * Only the basename is matched, which is the same shape written into the spec
   * so a downloaded file runs beside its boards under the CLI.
   */
  provider(): SourceProvider {
    return {
      resolve: (file, source) => {
        const entry = this.sources.get(basename(file));
        if (entry === undefined) return null;
        return resolveSource(entry.polygons, entry.annotations, source, entry.maxEdgeWidth);
      },
    };
  }
}

export function basename(path: string): string {
  const parts = path.split(/[\\/]/);
  return parts[parts.length - 1] ?? path;
}

/** A board name not already used in this spec, suffixed as needed. */
export function uniqueBoardName(spec: PanelSpec, base: string): string {
  const used = new Set((spec.boards ?? []).map((b) => b.name).filter(Boolean));
  if (!used.has(base)) return base;
  for (let n = 2; ; n++) {
    const candidate = `${base}_${n}`;
    if (!used.has(candidate)) return candidate;
  }
}
